import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import {
  fetchTokenEarningConfig,
  updateTokenEarningConfig,
  uploadTokenEarningIcon,
} from '../services/tokenEarningConfigApi';
import Alert from './Alert';
import PermissionGate from './PermissionGate';

const DEFAULT_CONFIG = {
  title: 'How to earn Got Tokens',
  subtitle: 'Every order earns tokens on the net amount after discounts',
  items: [
    {
      id: 'drink_pct',
      label: 'Drinks',
      description: '4% of the discounted net amount',
      ratePercent: 4,
      valueLabel: '4%',
      valueUnit: 'of net',
      icon: 'drink',
      iconUrl: '',
    },
    {
      id: 'food_pct',
      label: 'Food & Desserts',
      description: '3% of the discounted net amount',
      ratePercent: 3,
      valueLabel: '3%',
      valueUnit: 'of net',
      icon: 'food',
      iconUrl: '',
    },
  ],
  pageFootnote: 'Got Tokens are rounded to the nearest decimal.',
  sheetFootnote:
    'Got Tokens are rounded to the nearest decimal and added automatically when you scan your QR at checkout.',
  enabled: true,
};

function makeRow(index) {
  return {
    id: `earning_row_${Date.now()}_${index}`,
    label: '',
    description: '',
    ratePercent: '',
    valueLabel: '',
    valueUnit: 'of net',
    icon: 'drink',
    iconUrl: '',
  };
}

function formatRatePercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return `${number.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

function normalizeFromServer(config) {
  const serverItems = Array.isArray(config?.items) && config.items.length > 0
    ? config.items
    : DEFAULT_CONFIG.items;

  return {
    ...DEFAULT_CONFIG,
    ...(config || {}),
    items: serverItems.map((item, index) => ({
      ...makeRow(index),
      ...item,
      id: item.id || `earning_row_${index + 1}`,
      ratePercent: item.ratePercent === null || item.ratePercent === undefined ? '' : String(item.ratePercent),
      valueLabel: item.valueLabel || formatRatePercent(item.ratePercent),
      icon: ['drink', 'food'].includes(item.icon) ? item.icon : 'drink',
      iconUrl: item.iconUrl || '',
    })),
    enabled: config?.enabled !== false,
  };
}

function validate(form) {
  const errors = [];
  const requiredText = [
    ['Title', form.title],
    ['Subtitle', form.subtitle],
    ['Rewards page footnote', form.pageFootnote],
    ['Bottom sheet footnote', form.sheetFootnote],
  ];

  requiredText.forEach(([label, value]) => {
    if (!String(value || '').trim()) errors.push(`${label} is required.`);
  });

  if (form.items.length === 0) errors.push('Add at least one display row.');

  const items = form.items.map((item, index) => {
    const ratePercent = item.ratePercent === '' ? null : Number(item.ratePercent);
    if (item.ratePercent !== '' && (!Number.isFinite(ratePercent) || ratePercent < 0 || ratePercent > 100)) {
      errors.push(`Row ${index + 1}: display percent must be between 0 and 100.`);
    }
    if (!String(item.label || '').trim()) errors.push(`Row ${index + 1}: label is required.`);
    if (!String(item.description || '').trim()) errors.push(`Row ${index + 1}: description is required.`);
    if (!String(item.valueLabel || '').trim()) errors.push(`Row ${index + 1}: value label is required.`);
    if (!String(item.valueUnit || '').trim()) errors.push(`Row ${index + 1}: value unit is required.`);

    return {
      id: item.id || `earning_row_${index + 1}`,
      label: item.label.trim(),
      description: item.description.trim(),
      ratePercent,
      valueLabel: item.valueLabel.trim(),
      valueUnit: item.valueUnit.trim(),
      icon: ['drink', 'food'].includes(item.icon) ? item.icon : 'drink',
      iconUrl: item.iconUrl || '',
    };
  });

  return {
    errors,
    payload: {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      items,
      pageFootnote: form.pageFootnote.trim(),
      sheetFootnote: form.sheetFootnote.trim(),
      enabled: form.enabled,
    },
  };
}

function TokenEarningConfig() {
  const [form, setForm] = useState(() => normalizeFromServer(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIconIndex, setUploadingIconIndex] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await fetchTokenEarningConfig();
      setForm(normalizeFromServer(cfg));
    } catch (err) {
      setAlertMessage(err.message || 'Failed to load earning display.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const previewItems = useMemo(() => {
    return form.items.map((item) => ({
      ...item,
      valueLabel: item.valueLabel || formatRatePercent(item.ratePercent),
    }));
  }, [form.items]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateItem = (idx, field, value) => {
    setForm((prev) => {
      const items = prev.items.slice();
      const nextItem = { ...items[idx], [field]: value };
      if (field === 'ratePercent') nextItem.valueLabel = formatRatePercent(value);
      items[idx] = nextItem;
      return { ...prev, items };
    });
  };

  const addRow = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, makeRow(prev.items.length + 1)] }));
  };

  const removeRow = (idx) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, index) => index !== idx) }));
  };

  const handleIconUpload = async (idx, file) => {
    if (!file) return;
    setUploadingIconIndex(idx);
    try {
      const result = await uploadTokenEarningIcon(file);
      updateItem(idx, 'iconUrl', result.iconUrl || '');
      setAlertMessage('Icon uploaded. Click Save to persist this row.');
    } catch (err) {
      setAlertMessage(err.message || 'Failed to upload icon.');
    } finally {
      setUploadingIconIndex(null);
    }
  };

  const restoreDefaults = () => {
    setForm(normalizeFromServer(null));
    setAlertMessage('Restored defaults locally. Click Save to persist.');
  };

  const handleSave = async () => {
    const { errors, payload } = validate(form);
    if (errors.length > 0) {
      setAlertMessage(errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      const saved = await updateTokenEarningConfig(payload);
      setForm(normalizeFromServer(saved));
      setLastSavedAt(new Date());
      setAlertMessage('Earning display saved.');
    } catch (err) {
      setAlertMessage(err.message || 'Failed to save earning display.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && (
        <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Earning Display</h2>
          <p className="text-gray-500 mt-1">
            Configure the app copy only. This does not change actual Got Token earning logic.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
            disabled={loading || saving || uploadingIconIndex !== null}
          >
            <ArrowPathIcon className="size-5" />
            Reload
          </button>
          <PermissionGate permission="loyalty.earning_rules.update"><button
            type="button"
            onClick={handleSave}
            className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60"
            disabled={loading || saving || uploadingIconIndex !== null}
          >
            <CheckIcon className="size-5" />
            {saving ? 'Saving...' : 'Save'}
          </button></PermissionGate>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading earning display...</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded shadow p-5">
              <h3 className="text-lg font-bold mb-4">Display Copy</h3>
              <div className="grid gap-4">
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    className="w-full border px-3 py-2 rounded"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Subtitle</span>
                  <input
                    type="text"
                    value={form.subtitle}
                    onChange={(e) => updateField('subtitle', e.target.value)}
                    className="w-full border px-3 py-2 rounded"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Rewards page footnote</span>
                  <input
                    type="text"
                    value={form.pageFootnote}
                    onChange={(e) => updateField('pageFootnote', e.target.value)}
                    className="w-full border px-3 py-2 rounded"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1">Bottom sheet footnote</span>
                  <textarea
                    value={form.sheetFootnote}
                    onChange={(e) => updateField('sheetFootnote', e.target.value)}
                    className="w-full border px-3 py-2 rounded"
                    rows="3"
                  />
                </label>
              </div>
            </div>

            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between gap-4 mb-2">
                <h3 className="text-lg font-bold">App Display Rows</h3>
                <button
                  type="button"
                  onClick={addRow}
                  className="text-teal-500 hover:opacity-70 flex items-center gap-1 text-sm font-medium"
                >
                  <PlusIcon className="size-4" />
                  Add row
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                These rows are visual only. Actual earning remains fixed in the API order-award logic.
              </p>
              <div className="space-y-4">
                {form.items.map((item, idx) => (
                  <div key={item.id || idx} className="border rounded p-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="font-semibold">Row {idx + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="text-red-500 hover:opacity-70 disabled:opacity-40"
                        disabled={form.items.length === 1}
                        aria-label={`Remove row ${idx + 1}`}
                      >
                        <TrashIcon className="size-5" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="block text-sm font-medium mb-1">Label</span>
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e) => updateItem(idx, 'label', e.target.value)}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="All orders"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-medium mb-1">Value label</span>
                        <input
                          type="text"
                          value={item.valueLabel}
                          onChange={(e) => updateItem(idx, 'valueLabel', e.target.value)}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="8%"
                        />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="block text-sm font-medium mb-1">Description</span>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(idx, 'description', e.target.value)}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="8% of the discounted net amount"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-medium mb-1">Value unit</span>
                        <input
                          type="text"
                          value={item.valueUnit}
                          onChange={(e) => updateItem(idx, 'valueUnit', e.target.value)}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="of net"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-medium mb-1">Display percent metadata</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.ratePercent}
                          onChange={(e) => updateItem(idx, 'ratePercent', e.target.value)}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="Optional"
                        />
                      </label>
                      <div className="md:col-span-2">
                        <span className="block text-sm font-medium mb-1">Icon</span>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="size-12 border rounded bg-gray-50 flex items-center justify-center overflow-hidden text-xs font-bold text-teal-600">
                            {item.iconUrl ? (
                              <img src={item.iconUrl} alt="" className="max-h-10 max-w-10 object-contain" />
                            ) : (
                              <span>{item.icon === 'food' ? 'Food' : 'Cup'}</span>
                            )}
                          </div>
                          <select
                            value={item.icon}
                            onChange={(e) => updateItem(idx, 'icon', e.target.value)}
                            className="border px-3 py-2 rounded"
                          >
                            <option value="drink">Drink fallback</option>
                            <option value="food">Food fallback</option>
                          </select>
                          <label className="border px-3 py-2 rounded text-sm cursor-pointer hover:bg-gray-50">
                            {uploadingIconIndex === idx ? 'Uploading...' : 'Upload icon'}
                            <input
                              type="file"
                              accept="image/svg+xml,image/png,image/webp,image/jpeg"
                              className="hidden"
                              disabled={uploadingIconIndex === idx}
                              onChange={(e) => {
                                handleIconUpload(idx, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {item.iconUrl && (
                            <button
                              type="button"
                              onClick={() => updateItem(idx, 'iconUrl', '')}
                              className="text-sm text-gray-500 hover:underline"
                            >
                              Clear uploaded icon
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between items-center gap-4">
                <label className="inline-flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => updateField('enabled', e.target.checked)}
                    className="size-5 accent-teal-500"
                  />
                  <span className="font-medium">{form.enabled ? 'Enabled' : 'Disabled'}</span>
                </label>
                <button
                  type="button"
                  onClick={restoreDefaults}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Restore default rows
                </button>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="bg-white rounded shadow p-5">
              <h3 className="text-lg font-bold mb-3">App Preview</h3>
              <div>
                <h4 className="font-bold text-gray-900">{form.title}</h4>
                <p className="text-sm text-gray-500 mt-1">{form.subtitle}</p>
                <div className="mt-4 divide-y">
                  {previewItems.map((item, idx) => (
                    <div key={item.id || idx} className="py-3 flex items-center gap-3">
                      <div className="size-9 rounded bg-teal-50 text-teal-600 flex items-center justify-center overflow-hidden text-xs font-bold">
                        {item.iconUrl ? (
                          <img src={item.iconUrl} alt="" className="max-h-7 max-w-7 object-contain" />
                        ) : (
                          <span>{item.icon === 'food' ? 'Food' : 'Cup'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{item.label || 'Untitled row'}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-teal-600">{item.valueLabel}</div>
                        <div className="text-xs text-gray-500">{item.valueUnit}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 text-center mt-4">{form.pageFootnote}</p>
              </div>
            </div>

            <div className="bg-white rounded shadow p-5 text-sm text-gray-600 space-y-2">
              <h3 className="text-base font-bold text-gray-900">How it works</h3>
              <p>Saving updates <code className="bg-gray-100 px-1 rounded">config/tokenEarning</code>.</p>
              <p>This controls only the app display. Actual Got Token earning still uses the API order-award constants.</p>
              {lastSavedAt && (
                <p className="text-xs text-gray-500">Last saved: {lastSavedAt.toLocaleString()}</p>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default TokenEarningConfig;
